/* src/components/RuleEditForm.js */
import React, { useState, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import './RuleEditForm.css';

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['clean']
  ],
};

function RuleEditForm({ rule, allRules, onSave, onCancel, onDelete }) {
  const [formData, setFormData] = useState({
    ruleName: rule?.ruleName || '',
    content_html: rule?.content_html || '',
    keynote: rule?.keynote || '', 
  });
  const [path, setPath] = useState(rule?.displayPath || []);
  const [newHeadingInputs, setNewHeadingInputs] = useState({});

  const hierarchy = useMemo(() => {
    const root = { children: {} };
    if (!allRules) return root;
    allRules.forEach(r => {
      let currentLevel = root;
      const rPath = r.displayPath && r.displayPath.length > 0 ? r.displayPath : ['Uncategorized'];
      rPath.forEach((segment) => {
        if (!currentLevel.children[segment]) {
          currentLevel.children[segment] = { children: {} };
        }
        currentLevel = currentLevel.children[segment];
      });
    });
    return root;
  }, [allRules]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleContentChange = (html) => {
    setFormData(prev => ({ ...prev, content_html: html }));
  };

  const handlePathChange = (levelIndex, value) => {
    const newPath = path.slice(0, levelIndex);
    const newHeadings = { ...newHeadingInputs };
    delete newHeadings[levelIndex];

    if (value && value !== '__ADD_NEW__') {
      newPath.push(value);
    }
    
    if (value === '__ADD_NEW__') {
      newHeadings[levelIndex] = '';
    }

    setPath(newPath);
    setNewHeadingInputs(newHeadings);
  };

  const handleNewHeadingChange = (levelIndex, value) => {
    setNewHeadingInputs(prev => ({ ...prev, [levelIndex]: value }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!formData.ruleName || !formData.content_html) {
      alert('Rule Name and Content cannot be empty.');
      return;
    }

    const cleanHtml = DOMPurify.sanitize(formData.content_html);

    const finalPath = [];
    for (let i = 0; i < path.length + 1; i++) {
        if (newHeadingInputs.hasOwnProperty(i) && newHeadingInputs[i]) {
            finalPath.push(newHeadingInputs[i]);
        } else if (path[i]) {
            finalPath.push(path[i]);
        }
    }
    
    onSave({
      ...rule,
      ruleName: formData.ruleName,
      content_html: cleanHtml,
      keynote: formData.keynote,
      displayPath: finalPath,
      content_plain: cleanHtml.replace(/<[^>]+>/g, '').replace(/\[(.*?)\]/g, ''),
    });
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to permanently delete the rule "${rule.ruleName}"?`)) {
      onDelete(rule.id);
    }
  };

  const renderPathSelectors = () => {
    const selectors = [];
    let currentLevel = hierarchy;
    const numSelectors = path.length + 1; 

    for (let i = 0; i < numSelectors; i++) {
      const options = Object.keys(currentLevel.children);
      const selectedValue = path[i] || '';

      selectors.push(
        <div key={i} className="path-segment">
          <label>Level {i + 1} Heading</label>
          <div className="path-input-row">
            <select
              value={newHeadingInputs.hasOwnProperty(i) ? '__ADD_NEW__' : selectedValue}
              onChange={(e) => handlePathChange(i, e.target.value)}
            >
              <option value="">-- Select Heading --</option>
              {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              <option value="__ADD_NEW__">-- Add New Heading --</option>
            </select>
            {newHeadingInputs.hasOwnProperty(i) && (
              <input
                type="text"
                placeholder="New Heading Name"
                className="new-heading-input"
                value={newHeadingInputs[i]}
                onChange={(e) => handleNewHeadingChange(i, e.target.value)}
                autoFocus
              />
            )}
          </div>
        </div>
      );

      if (selectedValue && currentLevel.children[selectedValue]) {
        currentLevel = currentLevel.children[selectedValue];
      } else {
        break;
      }
    }
    return selectors;
  };

  return (
    <form className="rule-edit-form" onSubmit={handleSave}>
      <div className="form-group">
        <label htmlFor="ruleName">Rule Name</label>
        <input 
            type="text" 
            id="ruleName" 
            name="ruleName" 
            value={formData.ruleName} 
            onChange={handleFormChange} 
            required 
            placeholder="e.g. Battery: Elements"
        />
      </div>
      
      <div className="form-group hierarchy-group">
        <label style={{marginBottom:'15px', color:'var(--text-color)'}}>Classification Path</label>
        {renderPathSelectors()}
      </div>

      <div className="form-group">
        <label htmlFor="content_html">Rule Content (Use [brackets] for clozes)</label>
        <ReactQuill 
          theme="snow"
          value={formData.content_html}
          onChange={handleContentChange}
          modules={quillModules}
        />
      </div>

      {/* NEW: Keynote Editor Section */}
      <div className="form-group keynote-edit-group">
        <label htmlFor="keynote">My Keynote (Mnemonic/Hint)</label>
        <textarea
            id="keynote"
            name="keynote"
            className="keynote-input-area"
            value={formData.keynote}
            onChange={handleFormChange}
            placeholder="Add a personal memory hook, acronym, or simple explanation..."
        />
      </div>

      <div className="form-actions">
        {rule && (
          <button type="button" onClick={handleDelete} className="delete-btn">
            Delete Rule
          </button>
        )}
        <div className="main-actions-group">
            <button type="button" onClick={onCancel} className="btn" style={{backgroundColor:'transparent', color:'var(--text-muted)', boxShadow:'none', border:'1px solid var(--border-color)'}}>Cancel</button>
            <button type="submit" className="btn">Save Rule</button>
        </div>
      </div>
    </form>
  );
}

export default RuleEditForm;